import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  OrganizationsByRoleInterface,
  PaymentMethodInterface,
  PersonInterface,
  PostalAddressInterface,
  UserInterface,
} from '@sport-activities/sdk';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

import { LoginState } from '~/auth/interface/login-state.interface';
import { User } from '~/auth/model/user';
import { PostalAddress } from '~/checkout/model/postal-address.model';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasUser());
  // Emitted after login
  public loggedIn$ = new Subject<LoginState | undefined>();
  public user: UserInterface;

  constructor(
    @Inject(ACTIVITIES_BASE_URL) private readonly baseUrl: string,
    private readonly http: HttpClient
  ) {}

  // delete user credits card by paymentMethod
  public deleteCreditCard(
    paymentMethod: string
  ): Observable<PaymentMethodInterface[]> {
    return this.http.delete<PaymentMethodInterface[]>(
      `${this.baseUrl}/me/payment-methods/${paymentMethod}`
    );
  }

  // retrieve user address
  public getAddress(): Observable<PostalAddress> {
    return this.http
      .get<PostalAddressInterface>(`${this.baseUrl}/me/address`)
      .pipe(mergeMap((address) => of(new PostalAddress(address))));
  }

  // retrieve user credits cards
  public getCreditCardCollection(): Observable<PaymentMethodInterface[]> {
    return this.http.get<PaymentMethodInterface[]>(
      `${this.baseUrl}/me/payment-methods`
    );
  }

  // retrieve user organizations
  public getOrganizations(): Observable<OrganizationsByRoleInterface[]> {
    return this.http.get<OrganizationsByRoleInterface[]>(
      `${this.baseUrl}/me/organizations`
    );
  }

  public getUser(): User {
    if (!this.user) {
      return;
    }

    return new User(this.user);
  }

  public hasOrganizations(): Observable<boolean> {
    return this.isAuthenticated().pipe(
      mergeMap((authenticated) =>
        authenticated
          ? this.getOrganizations().pipe(
              map((organizations) => {
                return !!organizations[0]?.organizations.length;
              })
            )
          : of(false)
      )
    );
  }

  public hasUser(): boolean {
    return !!this.user;
  }

  public isAuthenticated(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  public setUser(user?: UserInterface): void {
    this.user = user;
    this.isAuthenticatedSubject.next(this.hasUser());
  }

  // update (or create) user address
  public updateAddress(
    address: PostalAddressInterface
  ): Observable<PostalAddressInterface> {
    return this.http.put<PostalAddressInterface>(
      `${this.baseUrl}/me/address`,
      address
    );
  }

  public updateUser(user: PersonInterface): Observable<UserInterface> {
    return this.http
      .patch<UserInterface>(`${this.baseUrl}/me`, user)
      .pipe(tap((userResponse) => this.setUser(userResponse)));
  }

  public updateUserTokens(
    accessToken: string,
    refreshToken: string
  ): Observable<User> {
    return this.http.put<User>(`${this.baseUrl}/me/tokens`, {
      accessToken: accessToken,
      refreshToken: refreshToken,
    });
  }
}
